import { Link } from 'react-router-dom';
import { Mail, MapPin, Camera, Plane, Shield, Clock } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
);
const PinterestIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
);

export const Footer = () => {
  const c = useSiteContent('footer');
  const currentYear = new Date().getFullYear();

  const serviceAreas = [
    'Byron Bay',
    'Bangalow',
    'Mullumbimby',
    'Lennox Head',
    'Ballina',
    'Gold Coast',
    'Northern Rivers',
  ];

  const services = [
    { name: 'Real Estate Photography', href: '/real-estate' },
    { name: 'Editorial Photography', href: '/editorial' },
    { name: 'Drone Aerial Photography', href: '/real-estate' },
    { name: 'Cinematic Property Films', href: '/real-estate' },
    { name: 'Floor Plans', href: '/real-estate' },
  ];

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/about#contact' },
    { name: 'Fine Art Prints', href: '/fine-art-prints', bold: true },
  ];

  return (
    <footer className="bg-charcoal text-cream" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src="/logo.png" alt="Balduzzi Living" width={125} height={34} loading="lazy" className="h-8 brightness-0 invert" />
            </Link>
            <p className="text-cream/70 mb-6 text-sm leading-relaxed">
              {c('tagline', 'Professional real estate and editorial property photography serving Byron Bay and the Northern Rivers region.')}
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 text-xs text-cream/60">
              <span className="flex items-center gap-1">
                <Camera className="w-4 h-4" />
                Professional Gear
              </span>
              <span className="flex items-center gap-1">
                <Plane className="w-4 h-4" />
                Registered Drone Operator
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                Fully Insured
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                24-48hr Delivery
              </span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-serif text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-cream/70 hover:text-cream text-sm transition-colors duration-300"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas - SEO Optimized */}
          <div>
            <h4 className="font-serif text-lg mb-6">Service Areas</h4>
            <ul className="space-y-3">
              {serviceAreas.map((area) => (
                <li key={area}>
                  <span className="text-cream/70 text-sm flex items-center gap-2">
                    <MapPin className="w-3 h-3" />
                    {area}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-cream/50 text-xs mt-4">
              Serving across Australia and internationally.
            </p>
          </div>

          {/* Quick Links & Contact */}
          <div>
            <h4 className="font-serif text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3 mb-8">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className={`text-cream/70 hover:text-cream text-sm transition-colors duration-300 ${link.bold ? 'font-semibold text-cream' : ''}`}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            <h4 className="font-serif text-lg mb-4">Contact</h4>
            <a
              href="mailto:hello@balduzziliving.com"
              className="flex items-center gap-2 text-cream/70 hover:text-cream text-sm transition-colors duration-300 mb-4"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              hello@balduzziliving.com
            </a>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/balduzziliving/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/60 hover:text-cream transition-colors duration-300"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://pin.it/16Bsv8YAo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream/60 hover:text-cream transition-colors duration-300"
                data-testid="footer-pinterest"
                aria-label="Pinterest"
              >
                <PinterestIcon />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-cream/10">
        <div className="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-cream/50 text-xs">
            © {currentYear} Balduzzi Living. All rights reserved.
          </p>
          <p className="text-cream/50 text-xs">
            Real Estate & Editorial Property Photography | Byron Bay, NSW, Australia
          </p>
        </div>
      </div>
    </footer>
  );
};
